import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import {
  AgAnnualSignEditEmailComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-edit-email/ag-annual-sign-edit-email.component";
import {
  AgAnnualSignEditPhoneComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-edit-phone/ag-annual-sign-edit-phone.component";
import {
  AgDocumentsForSignComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-documents-for-sign/ag-documents-for-sign.component";
import { documentsQueryParamKey } from "@shared/constants/query-param-keys.constant";
import { ESignApiService } from "@shared/services/e-sign-api.service";
import { SignalRService } from "@shared/services/signalr.service";
import { Guid } from "@shared/utils/guid.helper";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { bulkSendDocumentsForSigningConfirmText } from "../../constants/bulk-send-for-signing.constant";
import { ColDef, GridApi, RowSelectionOptions } from "ag-grid-community";
import {
  AgBulkSignatoryStatusComponent
} from "@shared/components/grid/ag-cells/bulk-changes/ag-bulk-signatory-status/ag-bulk-signatory-status.component";
import { BulkSignatoryGroup, SendBulkEsignModel } from "@models/documents/sign-documents";
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { of, take } from "rxjs";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  BaseListGridComponent
} from "@shared/components/grid/components/base-list-grid/base-list-grid.component";

export interface BulkSendDocumentsConfirmControls {
  message: FormControl<string | null>,
  confirm: FormControl<boolean | null>,
}

@Component({
  selector: 'app-base-bulk-send-for-signing',
  template: '',
  standalone: true
})
export class BaseBulkSendForSigningComponent extends CommonPageComponent implements OnInit {
  route = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);
  esignService = inject(ESignApiService);
  toastr = inject(ToastrService);
  signalRService = inject(SignalRService);

  readonly confirmText = bulkSendDocumentsForSigningConfirmText;
  readonly phoneMinLen = 6;
  readonly maxMessageLen = 100000;
  readonly recipientsMessageCustomErrors = { maxlength: 'You\'ve exceeded the limit of ' + this.maxMessageLen + ' characters.' };
  readonly queryParamKey: string = documentsQueryParamKey;
  readonly rowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  signingBulkModelStatus: SigningStatus | null = null;
  documentsIds: string[] = [];
  rows: BulkSignatoryGroup[] = [];
  isDataInvalid = false;

  form = new FormGroup<BulkSendDocumentsConfirmControls>({
    message: new FormControl<string | null>(null, Validators.maxLength(this.maxMessageLen)),
    confirm: new FormControl<boolean | null>(null, Validators.requiredTrue),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenQueryChanges();
  }

  listenQueryChanges(queryParamKey = this.queryParamKey): void {
    this.route.queryParams
      .pipe(take(1))
      .subscribe({
        next: (params) => {
          this.documentsIds = [params[queryParamKey]]
            .flat()
            .filter((documentId) => documentId && Guid.isValidGuid(documentId)) as string[];

          this.loadEntities(this.documentsIds);
        }
      });
  }

  loadEntities(documentsIds = this.documentsIds): void {
    this.isLoading.set(true);

    this.esignService.getEsignBulkModel(documentsIds)
      .subscribe({
        next: (data) => {
          this.signingBulkModelStatus = data.signingBulkModelStatus ?? null;

          this.rows = data.signatoryGroups;
          this.setGridConfig(data.signingBulkModelStatus !== SigningStatus.ReadyToSend);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.toastr.error("An error occurred.", "Error");
          console.error(err);
          this.isLoading.set(false);
          return of(null);
        }
      });
  }

  buildPayload(): SendBulkEsignModel | null {
    const signatoriesHasEmail = this.rows.every((row) => row.email);

    if (!signatoriesHasEmail) {
      this.toastr.error('Signatories email is required', 'Error');
      return null;
    }

    const eSignBody: SendBulkEsignModel = {
      documentsId: this.documentsIds,
      signatoryGroups: this.rows,
      emailMessage: this.form.value.message ?? ''
    };

    return eSignBody;
  }

  bulkSendForSign(): void {
    const eSignBody = this.buildPayload();
    if (!eSignBody) return;

    this.isLoading.set(true);
    setControlDisabled(this.form);

    this.esignService.sendToEsignBulk(eSignBody)
      .subscribe({
        next: (data) => {
          this.signingBulkModelStatus = data.signingBulkModelStatus ?? null;
          this.rows = data.signatoryGroups;

          this.setGridConfig(data.signingBulkModelStatus !== SigningStatus.ReadyToSend);
          this.gridApi?.refreshCells({ force: true });

          this.toastr.success("Documents sent for signing.", "Success");

          setControlDisabled(this.form, false);
          this.isLoading.set(false);
        },
        error: (error) => {
          this.toastr.error("Error happen.", "Error");
          console.error('[sendToEsignBulk] error', error);

          this.isLoading.set(false);
          setControlDisabled(this.form, false);

        }
      });
  }

  // ===== ===== ===== grid ===== ===== =====

  gridApi!: GridApi;
  colDefs: ColDef[] = [];

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  setGridConfig(hideEditableColumns = true): void {
    this.colDefs = [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        menuTabs: [],
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
        cellRenderer: hideEditableColumns ? null : AgAnnualSignEditEmailComponent,
        cellRendererParams: {
          edit: this.edit.bind(this),
        },
        menuTabs: [],
      },
      {
        headerName: 'Phone',
        field: 'phone',
        flex: 1,
        cellRenderer: hideEditableColumns ? null : AgAnnualSignEditPhoneComponent,
        cellRendererParams: {
          edit: this.edit.bind(this),
        },
        menuTabs: [],
      },
      {
        headerName: 'Status',
        field: 'signatoryStatus',
        width: 180,
        flex: 1,
        hide: !hideEditableColumns,
        editable: false,
        cellRenderer: AgBulkSignatoryStatusComponent,
        menuTabs: [],
      },
      {
        headerName: 'Documents For Signing',
        field: 'filesInfo',
        flex: 2,
        editable: false,
        autoHeight: true,
        cellRenderer: AgDocumentsForSignComponent,
        menuTabs: [],
      },
    ];
  }

  edit(data: { value: string, index: number; column: string }): void {
    let emailIsRequired = false;
    let invalidPhone = false;

    if (data.column === 'email') {
      this.rows[data.index].email = data.value;
      emailIsRequired = !this.rows?.every((row) => row.email);
    } else if (data.column === 'phone') {
      this.rows[data.index].phone = data.value;
      invalidPhone = this.rows
        .filter((row) => row.phone)
        ?.some((phoneNumber) => phoneNumber.phone.length < this.phoneMinLen);
    }

    this.isDataInvalid = emailIsRequired || invalidPhone;
  }

  // ===== ===== ===== signalr and related ===== ===== =====

  listenSignalRMessages(): void {
    this.signalRService
      .onDocumentSigningStatusChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          if (this.shouldRefreshSigningInfo(message) && this.signingBulkModelStatus !== null) {
            console.log('Refreshing signing info for message:', message);
            this.loadEntities();
          }
        },
        error: (err) => {
          console.error('Error handling SignalR message:', err);
        },
      });
  }

  shouldRefreshSigningInfo(message: string): boolean {
    return (
      this.documentsIds.some(x => x == message) &&
      this.signingBulkModelStatus !== SigningStatus.ReadyToSend
    );
  }

  get isReadyToSentStatus(): boolean {
    return this.signingBulkModelStatus === SigningStatus.ReadyToSend;
  }
}
